/**
 * Writing Streak Widget
 * Shows the logged-in user's current writing streak and the last 7 days at a glance.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Flame } from 'lucide-react';
import { useAuth } from '../../context/AuthenticationContext';
import { computeWritingStreak } from '../../utils/computeWritingStreak';

const DAY_MS = 86400000;
const dayKey = (ms) => new Date(ms).toISOString().slice(0, 10);

const WritingStreakWidget = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    if (!user?.id) return undefined;
    let active = true;
    axios
      .get(`/api/users/${user.id}`)
      .then((res) => { if (active) setPosts(res.data.posts || []); })
      .catch(() => { if (active) setPosts([]); });
    return () => { active = false; };
  }, [user?.id]);

  if (posts === null) return null; // don't flash an empty streak before posts load

  const streak = computeWritingStreak(posts);
  const days = new Set(
    posts
      .filter((p) => !p.repostOf && p.createdAt)
      .map((p) => dayKey(new Date(p.createdAt).getTime()))
  );
  const now = Date.now();
  const todayKey = dayKey(now);
  const last7 = Array.from({ length: 7 }, (_, i) => dayKey(now - (6 - i) * DAY_MS));

  return (
    <div className="prompt-widget streak-widget">
      <div className="widget-header">
        <span className="widget-icon"><Flame size={18} /></span>
        <span className="widget-title">Your streak</span>
      </div>

      <div className="streak-count">
        <span className="streak-number">{streak}</span>
        <span className="streak-unit">day{streak === 1 ? '' : 's'} streak</span>
      </div>

      <div className="streak-week" aria-hidden="true">
        {last7.map((k) => (
          <span
            key={k}
            className={`streak-dot ${days.has(k) ? 'filled' : (k === todayKey ? 'today' : '')}`}
          />
        ))}
      </div>
    </div>
  );
};

export default WritingStreakWidget;
